import { size } from "lodash";
import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from "react";

import {
  SKUPageContentGenericTabIcon,
  SKUPageContentTabType,
} from "@/data/types";

import { useCMSContext } from "./cms-context";

interface CMSTabContextType {
  tabType: SKUPageContentTabType;
  tabIndex: number;
  canDelete: boolean;
  isExpanded: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onCustomize: (
    title: string | undefined,
    icon: SKUPageContentGenericTabIcon | undefined,
  ) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onExpand: () => void;
  onDelete: () => void;
}

const CMSTabContext = createContext<CMSTabContextType | null>(null);

type Props = PropsWithChildren<{
  tabIndex: number;
  tabType: SKUPageContentTabType;
}>;

export const CMSTabProvider: FC<Props> = ({ tabIndex, tabType, children }) => {
  const {
    pageContent,
    activeTabIndex,
    onMoveTabUp,
    onMoveTabDown,
    onCustomizeTab,
    onExpandTab,
    onDeleteTab,
  } = useCMSContext();

  const isExpanded = useMemo(
    () => tabIndex === activeTabIndex,
    [tabIndex, activeTabIndex],
  );

  const canMoveUp = useMemo(() => {
    const isFirstTab = tabIndex === 0;

    return !isFirstTab;
  }, [tabIndex]);

  const canMoveDown = useMemo(() => {
    if (tabType === SKUPageContentTabType.PRODUCT_TAB) {
      return false;
    }

    const isLastTab = tabIndex === size(pageContent.data) - 1;

    return !isLastTab;
  }, [tabIndex, pageContent.data, tabType]);

  const onMoveUp = useCallback(
    () => onMoveTabUp(tabIndex),
    [tabIndex, onMoveTabUp],
  );

  const onMoveDown = useCallback(
    () => onMoveTabDown(tabIndex),
    [tabIndex, onMoveTabDown],
  );

  const onCustomize = useCallback(
    (
      title: string | undefined,
      icon: SKUPageContentGenericTabIcon | undefined,
    ) => onCustomizeTab(tabIndex, title, icon),
    [tabIndex, onCustomizeTab],
  );

  const onExpand = useCallback(
    () => onExpandTab(tabIndex),
    [tabIndex, onExpandTab],
  );

  const onDelete = useCallback(
    () => onDeleteTab(tabIndex),
    [tabIndex, onDeleteTab],
  );

  const canDelete = useMemo(() => {
    return tabType !== SKUPageContentTabType.PRODUCT_TAB;
  }, [tabType]);

  return (
    <CMSTabContext.Provider
      value={{
        tabType,
        tabIndex,
        isExpanded,
        canMoveUp,
        canMoveDown,
        onMoveUp,
        onMoveDown,
        onCustomize,
        onExpand,
        canDelete,
        onDelete,
      }}
    >
      {children}
    </CMSTabContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useCMSTabContext = () => {
  const context = useContext(CMSTabContext);

  if (!context) {
    throw new Error("useCMSTabContext must be used within a CMSTabContext");
  }

  return context;
};
